
import { defineComponent, ref, computed, onMounted } from "vue";
import { Actions } from "@/store/enums/StoreEnums";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { ElMessage } from "element-plus";
import type { ElForm } from "element-plus";
import { PostcardRewardItem } from "@/store/modules/PostcardRewardModule";
import { POINT_LABEL } from "@/core/config/labels";

type FormInstance = InstanceType<typeof ElForm>;

interface ConfigForm {
  name: string;
  reward_type: string;
  rank_start: number | null;
  rank_end: number | null;
  threshold_count: number | null;
  min_water_multiplier: number;
  winner_take_highest: boolean;
  sort_order: number;
  status: string;
  postcard_reward_items_attributes: PostcardRewardItem[];
}

export default defineComponent({
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const formRef = ref<FormInstance>();

    const isNew = !route.params.id;

    onMounted(() => {
      if (isNew) {
        setCurrentPageBreadcrumbs("新建明信片奖励配置", [
          "明信片奖励管理;postcard_reward_configs",
        ]);
        // 新建模式：只加载选项
        loadOptions();
      } else {
        setCurrentPageBreadcrumbs("编辑明信片奖励配置", [
          "明信片奖励管理;postcard_reward_configs",
        ]);
        // 编辑模式：在 setup 中已处理加载顺序
      }
    });

    const config = ref<ConfigForm>({
      name: "",
      reward_type: "rank",
      rank_start: 1,
      rank_end: null,
      threshold_count: null,
      min_water_multiplier: 0,
      winner_take_highest: true,
      sort_order: 0,
      status: "active",
      postcard_reward_items_attributes: [],
    });

    const productOptions = ref<
      Array<{ id: number; name: string; recycle_price: number }>
    >([]);
    const couponOptions = ref<
      Array<{
        id: number;
        name: string;
        coupon_type: string;
        discount_value: number;
        validity_days: number;
      }>
    >([]);
    const productSearchLoading = ref(false);

    // 只显示未被删除的项
    const visibleItems = computed(() => {
      return config.value.postcard_reward_items_attributes.filter(
        (item) => !item._destroy
      );
    });

    // 远程搜索商品
    const searchProducts = (keyword: string) => {
      if (!keyword || keyword.length < 1) {
        return;
      }
      productSearchLoading.value = true;
      store
        .dispatch(Actions.SEARCH_POSTCARD_REWARD_PRODUCTS, keyword)
        .then(() => {
          productOptions.value =
            store.getters.currentPostcardRewardOptions.products;
        })
        .catch(() => {
          ElMessage.error("搜索商品失败");
        })
        .finally(() => {
          productSearchLoading.value = false;
        });
    };

    const loadOptions = () => {
      return store
        .dispatch(Actions.GET_POSTCARD_REWARD_OPTIONS)
        .then(() => {
          const options = store.getters.currentPostcardRewardOptions;
          // products 不再预加载，改为远程搜索
          couponOptions.value = options.coupons;
        })
        .catch(() => {
          ElMessage.error("获取选项失败");
        });
    };

    const loadConfig = () => {
      store
        .dispatch(Actions.GET_POSTCARD_REWARD_CONFIG, route.params.id)
        .then(() => {
          const data = store.getters.currentPostcardRewardConfig;
          config.value = {
            name: data.name,
            reward_type: data.reward_type,
            rank_start: data.rank_start,
            rank_end: data.rank_end,
            threshold_count: data.threshold_count,
            min_water_multiplier: data.min_water_multiplier || 0,
            winner_take_highest: data.winner_take_highest !== false,
            sort_order: data.sort_order,
            status: data.status,
            postcard_reward_items_attributes: (data.items || []).map(
              (item: any) => ({
                id: item.id,
                item_type: item.item_type,
                product_id: item.product_id,
                point_amount: item.point_amount,
                reward_rate: item.reward_rate || 0,
                coupon_id: item.coupon_id,
                quantity: item.quantity,
                sort_order: item.sort_order,
                _destroy: false,
                _reward_mode:
                  item.item_type === "point" && (item.reward_rate || 0) > 0
                    ? "rate"
                    : "fixed",
              })
            ),
          };
          // 编辑模式：将已有商品填充到选项中，以便下拉框显示
          const existingProducts = (data.items || [])
            .filter(
              (item: PostcardRewardItem) =>
                item.item_type === "product" && item.product_id
            )
            .map((item: PostcardRewardItem) => ({
              id: item.product_id,
              name: item.product_name || `商品#${item.product_id}`,
              recycle_price: 0,
            }));
          if (existingProducts.length > 0) {
            productOptions.value = existingProducts;
          }
        })
        .catch(() => {
          ElMessage.error("获取配置详情失败");
        });
    };

    // 在 onMounted 中已调用 loadOptions，编辑模式下先加载选项再加载配置
    if (!isNew) {
      // 使用 Promise 确保选项先加载完成
      loadOptions().then(() => {
        loadConfig();
      });
    }

    const rules = ref({
      name: [
        { required: true, message: "请输入配置名称", trigger: "blur" },
        {
          min: 1,
          max: 100,
          message: "名称长度需在 1-100 之间",
          trigger: "blur",
        },
      ],
      reward_type: [
        { required: true, message: "请选择奖励类型", trigger: "change" },
      ],
      threshold_count: [
        {
          validator: (rule: any, value: any, callback: any) => {
            if (
              config.value.reward_type === "threshold" &&
              (!value || value < 1)
            ) {
              callback(new Error("请输入有效的门槛数量"));
              return;
            }
            callback();
          },
          trigger: ["blur", "change"],
        },
      ],
    });

    const onRewardTypeChange = () => {
      if (config.value.reward_type === "rank") {
        config.value.threshold_count = null;
        config.value.rank_start = 1;
        config.value.rank_end = null;
      } else {
        config.value.rank_start = null;
        config.value.rank_end = null;
        config.value.threshold_count = 30;
      }
    };

    const addItem = (itemType: string) => {
      const newItem: any = {
        item_type: itemType,
        product_id: null,
        point_amount: itemType === "point" ? 100 : null,
        reward_rate: 0,
        coupon_id: null,
        quantity: 1,
        sort_order: config.value.postcard_reward_items_attributes.length,
        _destroy: false,
        _reward_mode: itemType === "point" ? "fixed" : undefined,
      };
      config.value.postcard_reward_items_attributes.push(newItem);
    };

    const onPointModeChange = (row: any, mode: string) => {
      if (mode === "fixed") {
        row.reward_rate = 0;
        row.point_amount = row.point_amount || 100;
      } else {
        row.point_amount = 0;
        row.reward_rate = row.reward_rate || 0.03;
      }
    };

    const removeItem = (index: number) => {
      // 找到对应的可见项
      const visibleIndex = index;
      let count = 0;
      for (
        let i = 0;
        i < config.value.postcard_reward_items_attributes.length;
        i++
      ) {
        if (!config.value.postcard_reward_items_attributes[i]._destroy) {
          if (count === visibleIndex) {
            const item = config.value.postcard_reward_items_attributes[i];
            if (item.id) {
              // 已存在的项标记为删除
              item._destroy = true;
            } else {
              // 新增的项直接移除
              config.value.postcard_reward_items_attributes.splice(i, 1);
            }
            break;
          }
          count++;
        }
      }
    };

    const onSubmit = (formEl: FormInstance | undefined) => {
      if (!formEl) return;

      formEl.validate((valid) => {
        if (valid) {
          // 验证奖励项
          const activeItems =
            config.value.postcard_reward_items_attributes.filter(
              (item) => !item._destroy
            );
          if (activeItems.length === 0) {
            ElMessage.error("请至少添加一个奖励项");
            return;
          }

          // 验证奖励项内容
          for (const item of activeItems) {
            if (item.item_type === "product" && !item.product_id) {
              ElMessage.error("请选择商品");
              return;
            }
            if (item.item_type === "point") {
              const usingRate = (item.reward_rate || 0) > 0;
              const usingFixed = (item.point_amount || 0) > 0;
              if (!usingRate && !usingFixed) {
                ElMessage.error(`请输入${POINT_LABEL}数量或百分比`);
                return;
              }
              if (usingRate && (item.reward_rate! <= 0 || item.reward_rate! > 1)) {
                ElMessage.error(`百分比必须在 0-1 之间`);
                return;
              }
            }
            if (item.item_type === "coupon" && !item.coupon_id) {
              ElMessage.error("请选择优惠券");
              return;
            }
          }

          // 验证排名奖励的排名范围
          if (config.value.reward_type === "rank") {
            if (!config.value.rank_start || config.value.rank_start < 1) {
              ElMessage.error("请输入有效的起始名次");
              return;
            }
            if (
              config.value.rank_end &&
              config.value.rank_end < config.value.rank_start
            ) {
              ElMessage.error("结束名次不能小于起始名次");
              return;
            }
          }

          const params = {
            name: config.value.name,
            reward_type: config.value.reward_type,
            rank_start:
              config.value.reward_type === "rank"
                ? config.value.rank_start
                : null,
            rank_end:
              config.value.reward_type === "rank"
                ? config.value.rank_end || config.value.rank_start
                : null,
            threshold_count:
              config.value.reward_type === "threshold"
                ? config.value.threshold_count
                : null,
            min_water_multiplier:
              config.value.reward_type === "rank"
                ? config.value.min_water_multiplier
                : 0,
            winner_take_highest:
              config.value.reward_type === "threshold"
                ? config.value.winner_take_highest
                : true,
            sort_order: config.value.sort_order,
            status: config.value.status,
            postcard_reward_items_attributes:
              config.value.postcard_reward_items_attributes.map((item: any) => ({
                id: item.id,
                item_type: item.item_type,
                product_id:
                  item.item_type === "product" ? item.product_id : null,
                point_amount:
                  item.item_type === "point" ? (item.point_amount || 0) : null,
                reward_rate:
                  item.item_type === "point" ? (item.reward_rate || 0) : 0,
                coupon_id: item.item_type === "coupon" ? item.coupon_id : null,
                quantity: item.item_type === "point" ? 1 : item.quantity,
                sort_order: item.sort_order,
                _destroy: item._destroy,
              })),
          };

          if (isNew) {
            store
              .dispatch(Actions.CREATE_POSTCARD_REWARD_CONFIG, params)
              .then(() => {
                ElMessage.success("创建成功");
                router.push({ name: "postcard_reward_configs" });
              })
              .catch(() => {
                ElMessage.error("创建失败");
              });
          } else {
            store
              .dispatch(Actions.UPDATE_POSTCARD_REWARD_CONFIG, {
                id: route.params.id,
                values: params,
              })
              .then(() => {
                ElMessage.success("更新成功");
                router.push({ name: "postcard_reward_configs" });
              })
              .catch(() => {
                ElMessage.error("更新失败");
              });
          }
        }
      });
    };

    const onCancel = () => {
      router.push({ name: "postcard_reward_configs" });
    };

    const pointLabel = POINT_LABEL;

    return {
      pointLabel,
      formRef,
      config,
      rules,
      isNew,
      productOptions,
      couponOptions,
      productSearchLoading,
      visibleItems,
      onRewardTypeChange,
      addItem,
      removeItem,
      onPointModeChange,
      searchProducts,
      onSubmit,
      onCancel,
    };
  },
});
